import App from "@bloglovin/inspire-engine/App";
import { useConfigurationStore } from "%/stores/configuration-store";
import useDynamicComponentEditorStore from "%/dynamic-component-editor/stores/dynamic-component-editor";
import { isComponentHiddenByFeatureClass } from "~/constants/feature-config";
import { useCompletionComponentsStore } from "%/dynamic-component-editor/stores/completion-components-store";
import StandardSurfaceSeeder from "%/dynamic-component-editor/classes/standard-surface-seeder";
import { useStudioStoryTypePagesStore } from "@studio/Stores/studio-story-type-pages-store";

const COMPONENT_TYPES = [undefined, null, "component"];

function readField(appName, componentPk, field) {
  return App.get(appName).getEntityField("DynamicComponent", componentPk, field);
}

function isInactive(appName, componentPk) {
  return (readField(appName, componentPk, "status") ?? "active") === "inactive";
}

function isNonComponentType(appName, componentPk) {
  return !COMPONENT_TYPES.includes(readField(appName, componentPk, "type"));
}

function isAppOwned(appName, componentPk) {
  return Boolean(readField(appName, componentPk, "appName"));
}

function isNonCanonicalVariant(componentPk) {
  return !useDynamicComponentEditorStore().isCanonicalVariantForGroup(componentPk.id);
}

function isStandardSurface(appName, componentPk) {
  return Boolean(StandardSurfaceSeeder.standardClassOf({
    name: readField(appName, componentPk, "name") ?? "",
    rootOptions: readField(appName, componentPk, "rootOptions"),
  }));
}

function standardSurfaceKey(appName, componentPk) {
  let rootOptions = readField(appName, componentPk, "rootOptions");
  if (typeof rootOptions === "string") {
    try {
      rootOptions = JSON.parse(rootOptions);
    } catch (error) {
      rootOptions = {};
    }
  }
  if (typeof rootOptions !== "object" || rootOptions === null) {
    return "";
  }
  if (rootOptions["standard-surface-key"]) {
    return rootOptions["standard-surface-key"];
  }
  return StandardSurfaceSeeder.standardSurfaceKeyForNameAndSlug(
    readField(appName, componentPk, "name"),
    rootOptions["page-slug"],
  );
}

function isFeatureDisabled(appName, componentPk) {
  return isComponentHiddenByFeatureClass(standardSurfaceKey(appName, componentPk), useConfigurationStore());
}

function belongsToVariantGroupOf(componentIds, componentPk) {
  return useDynamicComponentEditorStore()
      .variantGroupMembersForComponents(componentIds)
      .has(Number(componentPk.id));
}

export function isCompletionComponent(componentPk, name) {
  if (useCompletionComponentsStore().completionSlotForComponentName(name)) {
    return true;
  }
  return belongsToVariantGroupOf(useCompletionComponentsStore().componentIds, componentPk);
}

export function isStoryTypeComponent(componentPk, name) {
  const storyTypePagesStore = useStudioStoryTypePagesStore();
  if (storyTypePagesStore.storyTypeKeyForComponentName(name)) {
    return true;
  }
  const storyTypeComponents = storyTypePagesStore.storyTypeComponents ?? {};
  const componentIds = Object.values(storyTypeComponents).flat().map(component => Number(component.id));
  return belongsToVariantGroupOf(componentIds, componentPk);
}

export function isComponentRowVisible({ appName, componentPk, forceShow = false }) {
  if (!componentPk) {
    return false;
  }
  if (isInactive(appName, componentPk)) {
    return false;
  }
  if (isNonComponentType(appName, componentPk)) {
    return false;
  }
  if (isAppOwned(appName, componentPk)) {
    return false;
  }

  if (isFeatureDisabled(appName, componentPk)) {
    return false;
  }

  const name = readField(appName, componentPk, "name") ?? "";
  if (!forceShow && isStandardSurface(appName, componentPk)) {
    return false;
  }

  if (forceShow) {
    return true;
  }
  if (isNonCanonicalVariant(componentPk)) {
    return false;
  }
  return !isCompletionComponent(componentPk, name) && !isStoryTypeComponent(componentPk, name);
}

export function isComponentDeletedRow({ appName, componentPk }) {
  if (!componentPk) {
    return false;
  }
  if (!isInactive(appName, componentPk)) {
    return false;
  }
  if (isNonComponentType(appName, componentPk) || isAppOwned(appName, componentPk)) {
    return false;
  }
  if (isStandardSurface(appName, componentPk)) {
    return false;
  }
  return !isNonCanonicalVariant(componentPk);
}

export function isStandardLegacyComponentRow({ appName, componentPk }) {
  if (!componentPk) {
    return false;
  }
  if (isInactive(appName, componentPk)) {
    return false;
  }
  if (isNonComponentType(appName, componentPk)) {
    return false;
  }
  if (isAppOwned(appName, componentPk)) {
    return false;
  }
  if (isFeatureDisabled(appName, componentPk)) {
    return false;
  }
  return isStandardSurface(appName, componentPk);
}

export function usesCompletionContexts({ appName, componentPk }) {
  const name = readField(appName, componentPk, "name") ?? "";
  return isCompletionComponent(componentPk, name) || isStoryTypeComponent(componentPk, name);
}
